import { Link } from "react-router-dom";

import Badge from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import Typography from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { track } from "@/utils/mixpanel";

import { NavLinkProps } from "./types";

export const NavLink = ({
  item,
  isActive,
  collapsed,
  mobile,
  onClick,
}: NavLinkProps) => {
  const linkClassName = cn(
    // Base sizing and shape per DS
    "flex items-center gap-2 h-12 rounded-lg transition-colors duration-200 font-semibold",
    item.disabled
      ? "text-[#767677]/50 opacity-60 cursor-not-allowed"
      : isActive(item.path)
        ? "bg-[#dcddfa] text-[#4f55e8]"
        : "text-[#767677] hover:bg-[#f0f0f0]",
    collapsed && !mobile ? "justify-center px-3" : "px-6 py-4",
    item.className,
  );

  const wrapWithTooltip = (node: JSX.Element) => {
    if (!collapsed || mobile) {
      return node;
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{node}</TooltipTrigger>
          <TooltipContent side="right">
            <Typography size="l2" className="font-medium" as="span">
              {item.name}
            </Typography>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const handleClick = () => {
    // Don't track or navigate if disabled
    if (item.disabled) {
      return;
    }

    // If an action item provides its own handler, call it and stop
    if (item.onClick) {
      item.onClick();
      return;
    }

    // Track navigation
    track("navigate", {
      to: item.path,
      name: item.name,
      is_external: item.external || false,
    });

    // Call the original onClick if provided
    if (onClick) {
      onClick();
    }
  };

  // Treat items with onClick and no meaningful path as action buttons (so wrappers like DialogTrigger work reliably)
  const isAction = !!item.onClick && (!item.path || item.path === "#");

  if (isAction) {
    return wrapWithTooltip(
      <button type="button" className={linkClassName} onClick={handleClick}>
        {item.icon}
        {(!collapsed || mobile) && (
          <Typography size="l1" as="span">
            {item.name}
          </Typography>
        )}
        {(!collapsed || mobile) && item.badge && (
          <span className="ml-auto bg-[#4f55e8] text-white text-xs font-semibold px-1.5 py-0.5 rounded">
            {item.badge}
          </span>
        )}
      </button>,
    );
  }

  // If disabled, render as non-interactive div
  if (item.disabled) {
    return wrapWithTooltip(
      <div className={linkClassName}>
        {item.icon}
        {(!collapsed || mobile) && (
          <Typography size="l1" as="span">
            {item.name}
          </Typography>
        )}
        {(!collapsed || mobile) && item.badge && (
          <span className="ml-auto bg-[#4f55e8] text-white text-xs font-semibold px-1.5 py-0.5 rounded">
            {item.badge}
          </span>
        )}
      </div>,
    );
  }

  // Check if the link is external
  if (item.external) {
    return wrapWithTooltip(
      <a
        href={item.path}
        className={linkClassName}
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer"
      >
        {item.icon}
        {(!collapsed || mobile) && (
          <Typography size="l1" as="span">
            {item.name}
          </Typography>
        )}
        {(!collapsed || mobile) && item.badge && (
          <span className="ml-auto bg-[#4f55e8] text-white text-xs font-semibold px-1.5 py-0.5 rounded">
            {item.badge}
          </span>
        )}
      </a>,
    );
  }

  return wrapWithTooltip(
    <Link
      key={item.path}
      to={item.path}
      className={linkClassName}
      onClick={handleClick}
    >
      {item.icon}
      {(!collapsed || mobile) && (
        <Typography size="l1" as="span">
          {item.name}
        </Typography>
      )}
      {(!collapsed || mobile) && item.badge && (
        <span className="ml-auto">
          <Badge color="purple" variant="full">
            {item.badge}
          </Badge>
        </span>
      )}
    </Link>,
  );
};
